import { InboxOutlined, LinkOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Table,
  Typography,
  Upload,
} from 'antd';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { catalogueApi, type IngestionSummary, type Product } from '../catalogue.api';
import { ProductSourcesCard } from './ProductSourcesCard';

const addSchema = z.object({
  name: z.string().trim().min(1, 'Required'),
  sku: z.string().trim().optional(),
  amount: z.coerce.number().min(0),
});
type AddForm = z.infer<typeof addSchema>;

const summaryMsg = (s: IngestionSummary) =>
  `Created ${s.created}, updated ${s.updated}, failed ${s.failed}`;

export function CatalogueTab({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [csv, setCsv] = useState('');
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const key = ['products', orgId, projectId, page] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => catalogueApi.list(orgId, projectId, page, pageSize),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['products', orgId, projectId] });

  const form = useForm<AddForm>({
    resolver: zodResolver(addSchema),
    defaultValues: { name: '', sku: '', amount: 0 },
  });

  const addMutation = useMutation({
    mutationFn: (v: AddForm) =>
      catalogueApi.add(orgId, projectId, {
        name: v.name,
        ...(v.sku ? { sku: v.sku } : {}),
        price: { amount: v.amount, currency: 'USD' },
      }),
    onSuccess: (s) => {
      void invalidate();
      form.reset();
      message.success(summaryMsg(s));
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to add product'),
  });

  const csvMutation = useMutation({
    mutationFn: () => catalogueApi.importCsv(orgId, projectId, csv),
    onSuccess: (s) => {
      void invalidate();
      setCsv('');
      setCsvFileName(null);
      message.success(summaryMsg(s));
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'CSV import failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (productId: string) => catalogueApi.remove(orgId, projectId, productId),
    onSuccess: () => {
      void invalidate();
      message.success('Product deleted');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to delete'),
  });

  const bulkDelete = useMutation({
    mutationFn: (body: { productIds?: string[]; all?: boolean }) =>
      catalogueApi.bulkDelete(orgId, projectId, body),
    onSuccess: (r) => {
      setSelectedIds([]);
      setPage(1);
      void invalidate();
      message.success(`Deleted ${r.deleted} product(s)`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Bulk delete failed'),
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ProductSourcesCard orgId={orgId} projectId={projectId} canEdit={canEdit} />
      {canEdit ? (
        <Card size="small" title="Add product">
          <Form layout="inline" onFinish={form.handleSubmit((v) => addMutation.mutate(v))}>
            <Controller
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="Product name" style={{ width: 200 }} />
                </Form.Item>
              )}
            />
            <Controller
              control={form.control}
              name="sku"
              render={({ field }) => (
                <Form.Item>
                  <Input {...field} placeholder="SKU (optional)" style={{ width: 140 }} />
                </Form.Item>
              )}
            />
            <Controller
              control={form.control}
              name="amount"
              render={({ field }) => (
                <Form.Item>
                  <InputNumber {...field} min={0} prefix="$" placeholder="Price" />
                </Form.Item>
              )}
            />
            <Button type="primary" htmlType="submit" loading={addMutation.isPending}>
              Add
            </Button>
          </Form>
        </Card>
      ) : null}

      {canEdit ? (
        <Card size="small" title="Import CSV">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload.Dragger
              accept=".csv,text/csv"
              maxCount={1}
              fileList={csvFileName ? [{ uid: 'csv', name: csvFileName }] : []}
              beforeUpload={(file) => {
                void file.text().then(
                  (text) => {
                    setCsv(text);
                    setCsvFileName(file.name);
                  },
                  () => message.error('Could not read that file'),
                );
                // Never auto-upload — we send the parsed text through the API.
                return false;
              }}
              onRemove={() => {
                setCsv('');
                setCsvFileName(null);
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag a .csv file here</p>
              <p className="ant-upload-hint">
                Columns: name, price, currency, stock, sku, brand, url, image
              </p>
            </Upload.Dragger>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Supported columns: <code>brand</code> (aliases: vendor, manufacturer),{' '}
              <code>image</code> (aliases: images, image_url; separate multiple with <code>|</code>
              ), <code>url</code> (aliases: link, product_url). <code>currency</code> is optional —
              rows without it use your organization's settings currency.
            </Typography.Text>
            <Button
              type="primary"
              onClick={() => csvMutation.mutate()}
              loading={csvMutation.isPending}
              disabled={!csv.trim()}
            >
              Import {csvFileName ?? 'CSV'}
            </Button>
          </Space>
        </Card>
      ) : null}

      {canEdit ? (
        <Space wrap>
          <Popconfirm
            title={`Delete ${selectedIds.length} selected product(s)?`}
            onConfirm={() => bulkDelete.mutate({ productIds: selectedIds })}
            disabled={selectedIds.length === 0}
          >
            <Button danger disabled={selectedIds.length === 0} loading={bulkDelete.isPending}>
              Delete selected ({selectedIds.length})
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`Delete ALL ${query.data?.meta.total ?? 0} products in this project?`}
            description="This removes the entire catalogue and cannot be undone."
            okButtonProps={{ danger: true }}
            onConfirm={() => bulkDelete.mutate({ all: true })}
            disabled={(query.data?.meta.total ?? 0) === 0}
          >
            <Button danger disabled={(query.data?.meta.total ?? 0) === 0}>
              Delete all ({query.data?.meta.total ?? 0})
            </Button>
          </Popconfirm>
        </Space>
      ) : null}

      <Table<Product>
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        {...(canEdit
          ? {
              rowSelection: {
                selectedRowKeys: selectedIds,
                onChange: (keys: React.Key[]) => setSelectedIds(keys.map(String)),
                preserveSelectedRowKeys: true,
              },
            }
          : {})}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.meta.total ?? 0,
          onChange: setPage,
        }}
        columns={[
          {
            title: '',
            dataIndex: 'images',
            width: 56,
            render: (imgs: string[], p: Product) =>
              imgs[0] ? (
                <img
                  src={imgs[0]}
                  alt={p.name}
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
                />
              ) : (
                '—'
              ),
          },
          { title: 'Name', dataIndex: 'name' },
          { title: 'Brand', dataIndex: 'brand', render: (v: string) => v || '—' },
          { title: 'SKU', dataIndex: 'sku', render: (v: string | null) => v ?? '—' },
          { title: 'Category', dataIndex: 'category', render: (v: string) => v || '—' },
          {
            title: 'Price',
            dataIndex: 'price',
            render: (p: Product['price']) => `${p.currency} ${p.amount.toFixed(2)}`,
          },
          { title: 'Stock', dataIndex: 'stock' },
          {
            title: 'URL',
            dataIndex: 'url',
            width: 64,
            render: (v: string | null) =>
              v ? (
                <a href={v} target="_blank" rel="noreferrer">
                  <LinkOutlined />
                </a>
              ) : (
                '—'
              ),
          },
          ...(canEdit
            ? [
                {
                  title: '',
                  key: 'actions',
                  align: 'right' as const,
                  render: (_: unknown, p: Product) => (
                    <Popconfirm
                      title="Delete product?"
                      onConfirm={() => deleteMutation.mutate(p.id)}
                    >
                      <Button danger size="small">
                        Delete
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Space>
  );
}
